import { z } from 'zod';
import {
  loadValidatedSchema,
  EnvKeys,
  FieldPath,
} from '@getmoments/env42';
import { useMemo } from 'react';

export const AppConfigSchema = z.object({
  auth0Domain: z.string(),
  auth0ClientId: z.string(),
  auth0Audience: z.string(),
  loginRedirectUrl: z.string(),
  logoutRedirectUrl: z.string(),
  adminBackendUri: z.string(),
  azureDiagnosticsBlobPrefix: z.string(),
});

export type AppConfig = z.infer<typeof AppConfigSchema>;

export const AppConfigEnVarsSchema = z.enum([
  'NEXT_PUBLIC_AUTH0_DOMAIN',
  'NEXT_PUBLIC_AUTH0_CLIENT_ID',
  'NEXT_PUBLIC_AUTH0_AUDIENCE',
  'NEXT_PUBLIC_LOGIN_REDIRECT_URL',
  'NEXT_PUBLIC_LOGOUT_REDIRECT_URL',
  'NEXT_PUBLIC_ADMIN_BACKEND_URI',
  'NEXT_AZURE_DIAGNOSTICS_BLOB_PREFIX',
]);

export type AppConfigEnvVarNames = z.infer<
  typeof AppConfigEnVarsSchema
>;

export const appConfigEnvVarsMap: Record<
  FieldPath<AppConfig>,
  AppConfigEnvVarNames
> = {
  auth0Domain: 'NEXT_PUBLIC_AUTH0_DOMAIN',
  auth0ClientId: 'NEXT_PUBLIC_AUTH0_CLIENT_ID',
  auth0Audience: 'NEXT_PUBLIC_AUTH0_AUDIENCE',
  loginRedirectUrl: 'NEXT_PUBLIC_LOGIN_REDIRECT_URL',
  logoutRedirectUrl: 'NEXT_PUBLIC_LOGOUT_REDIRECT_URL',
  adminBackendUri: 'NEXT_PUBLIC_ADMIN_BACKEND_URI',
  azureDiagnosticsBlobPrefix: 'NEXT_AZURE_DIAGNOSTICS_BLOB_PREFIX',
};

export const loadAppConfigFromEnvironment = (
  env: EnvKeys<AppConfigEnvVarNames> = process.env as any,
) =>
  loadValidatedSchema(
    AppConfigSchema as any,
    appConfigEnvVarsMap,
    env,
  );

// Import the config from Vite-injected environment variables
// Vite's define option injects NEXT_PUBLIC_* vars as import.meta.env.* at build time
// This is baked into the bundle at build time
let appConfig: AppConfig | null = null;

// Get config from Vite-injected import.meta.env or require (for Node.js/config:lock)
const getEnvJsonData = (): AppConfig | null => {
  // In browser, use import.meta.env (injected by Vite at build time)
  if (typeof window !== 'undefined') {
    try {
      const config: AppConfig = {
        auth0Domain: import.meta.env.NEXT_PUBLIC_AUTH0_DOMAIN as string,
        auth0ClientId: import.meta.env.NEXT_PUBLIC_AUTH0_CLIENT_ID as string,
        auth0Audience: import.meta.env.NEXT_PUBLIC_AUTH0_AUDIENCE as string,
        loginRedirectUrl: import.meta.env.NEXT_PUBLIC_LOGIN_REDIRECT_URL as string,
        logoutRedirectUrl: import.meta.env.NEXT_PUBLIC_LOGOUT_REDIRECT_URL as string,
        adminBackendUri: import.meta.env.NEXT_PUBLIC_ADMIN_BACKEND_URI as string,
        azureDiagnosticsBlobPrefix: import.meta.env.NEXT_AZURE_DIAGNOSTICS_BLOB_PREFIX as string,
      };
      
      // Validate that all required fields are present
      if (config.auth0Domain && config.auth0ClientId) {
        return config;
      }
    } catch (err) {
      console.warn('Failed to load config from import.meta.env', err);
    }
  }
  
  // In Node.js (during config:lock or build), try to require it
  if (typeof require !== 'undefined') {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      return require('../../.env.json') as AppConfig;
    } catch (err) {
      // File doesn't exist yet (e.g., during config:lock execution)
      return null;
    }
  }
  
  return null;
};

const loadBakedConfig = (): AppConfig => {
  if (appConfig) {
    return appConfig;
  }

  // Try to get config from injected global or require
  const envJsonData = getEnvJsonData();
  if (envJsonData) {
    appConfig = envJsonData;
    return appConfig;
  }

  // Fall back to environment variables if JSON wasn't available
  appConfig = loadAppConfigFromEnvironment(process.env as any);
  return appConfig;
};

export const getAppConfig = (): AppConfig => {
  return loadBakedConfig();
};

export const useAppConfig = () => useMemo(() => getAppConfig(), []);
